/**
 * The period granularity a report is shown by (`monthly` / `quarterly` / `yearly`). The default is the
 * grain the response says it rendered at, so the rule lives in one place: the backend.
 */

import type { StarGridTextKey } from 'src/components/star-grid/hooks/useStarGridText';

export type ShowBy = 'monthly' | 'quarterly' | 'yearly';

/** Listed coarsest-last, which is how a period picker reads. */
export const SHOW_BY_OPTIONS: readonly { readonly value: ShowBy; readonly textKey: StarGridTextKey }[] = [
  { value: 'monthly', textKey: 'showByMonthly' },
  { value: 'quarterly', textKey: 'showByQuarterly' },
  { value: 'yearly', textKey: 'showByYearly' }
];

const KNOWN: readonly string[] = SHOW_BY_OPTIONS.map((option) => option.value);

/**
 * A wire value narrowed to a known grain, else `undefined`.
 *
 * An unrecognised grain is never guessed at — showing the picker on a value the server did not send
 * would misreport what the grid is actually displaying.
 */
export function asShowBy(value: string | null | undefined): ShowBy | undefined {
  const token = String(value ?? '')
    .trim()
    .toLowerCase();
  return KNOWN.includes(token) ? (token as ShowBy) : undefined;
}

export interface ShowByOption {
  readonly value: ShowBy;
  readonly label: string;
  readonly disabled: boolean;
}

/**
 * The picker's options, with any grain this report cannot honour DISABLED rather than hidden.
 *
 * Disabled and not removed, so the control always shows the full vocabulary: every current deck screen
 * states a single quarter, so `yearly` is unavailable on all of them, and silently dropping it would
 * read as a missing feature rather than an inapplicable one.
 */
export function showByOptions(label: (key: StarGridTextKey) => string, available: readonly string[] | undefined): ShowByOption[] {
  return SHOW_BY_OPTIONS.map((option) => ({
    value: option.value,
    label: label(option.textKey),
    // No list yet — the first render, before any response — disables nothing: greying every option
    // out while the report is still loading would make the control look broken.
    disabled: available !== undefined && !available.includes(option.value)
  }));
}
